import {
	useGetMyUserProfile,
	useSigninSignupMutation,
	useUserProfilesMutation
} from 'lib/services/userProfile.service'
import type React from 'react'
import type { ReactNode, JSX } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { IUserProfile } from 'models/userProfiles'
import { useMsal } from '@azure/msal-react'
import { ROLE_ADMIN, ROLE_BOOKING_BUDDY } from 'lib/helpers/roles'
import type { UserProfileContextValue } from './common'
import { UserProfileStatus } from './common'
import { LoggedInUserProfileContext } from 'lib/contexts/loggedInUserProfile.context'
import PageLoader from 'components/pageLoader'
import { getCookie } from 'lib/utils'

interface UserProfileProviderProperties {
	children: ReactNode
}

const LoggedInUserProfileProvider: React.FC<UserProfileProviderProperties> = ({
	children
}): JSX.Element => {
	const [userProfileStatus, setUserProfileStatus] = useState<UserProfileStatus>(
		UserProfileStatus.initial
	)

	const { accounts } = useMsal()

	const [userProfile, setUserProfile] = useState<IUserProfile>()
	const { mutateAsync: updateUserProfile } = useUserProfilesMutation()
	const { mutateAsync: signinSignup } = useSigninSignupMutation()
	const { refetch: getMyUserProfile } = useGetMyUserProfile(false)

	const memoizedValue: UserProfileContextValue = useMemo(() => {
		const useUpdatePreviewExperience = async ({
			userProfileId,
			previewExperienceKey,
			enable
		}: {
			userProfileId?: string
			previewExperienceKey: string
			enable: boolean
		}) => {
			const data = await updateUserProfile({
				profileId: userProfileId ?? userProfile?.id ?? '',
				previewExperienceKey,
				enable: enable ? 'true' : 'false'
			})
			return data
		}

		return {
			userProfile,
			setUserProfile,
			userProfileStatus,
			setUserProfileStatus,
			useUpdatePreviewExperience
		}
	}, [updateUserProfile, userProfile, userProfileStatus])

	useEffect(() => {
		console.log(`App accounts ${accounts.length}`)
		if (
			accounts.length > 0 &&
			userProfileStatus !== UserProfileStatus.loading
		) {
			setUserProfileStatus(UserProfileStatus.loading)
			signinSignup({ hutk: getCookie('hubspotutk') })
				.then(response => {
					const { status } = response
					if (status === 201) {
						console.log('created')
						globalThis.location.reload()
					} else {
						console.log('posted token')
						getMyUserProfile()
							.then(r => {
								if (r.data) {
									const isAdmin = [ROLE_ADMIN, ROLE_BOOKING_BUDDY].includes(
										r.data.role ?? ''
									)

									setUserProfile({
										...r.data,
										homeAccountId: accounts[0].homeAccountId,
										isAdmin,
										licenseTier: r.data.licenseInfo?.tier ?? '',
										licenseType: r.data.licenseInfo?.type ?? '',
										commissionVisible: r.data.commissionWaived
											? false
											: isAdmin ||
												(r.data.isPaidSubscriber &&
													(r.data.commissionEligible || r.data.rebateEligible))
									})
									setUserProfileStatus(UserProfileStatus.loaded)
								}
							})
							.catch((error: unknown) => {
								console.error(error)
							})
					}
				})
				.catch((error: unknown) => {
					console.error(error)
				})
		} else {
			setUserProfileStatus(UserProfileStatus.anonymous)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<LoggedInUserProfileContext.Provider value={memoizedValue}>
			{userProfile ? children : <PageLoader />}
		</LoggedInUserProfileContext.Provider>
	)
}

export default LoggedInUserProfileProvider
